import React from 'react';
import styles from './dialog.module.scss';
import clsx from 'clsx';

import Backdrop from '@smart-tv/components/backdrop';
import Portal from '@smart-tv/components/portal/portal';

import useDialog from '@smart-tv/hooks/use-dialog';
import useEventListener from '@smart-tv/hooks/use-event-listener';
import { IconButton } from '@smart-tv/components/icon-button/icon-button';
import ArrowBackIcon from '@smart-tv/components/icons/arrow-back-icon';

import { IS_BROWSER } from '@smart-tv/utils/is-browser';
import { testIds } from '@smart-tv/e2e/shared/variables';
import { isBackKey } from '@smart-tv/utils/remoteKeys';

interface DialogContextProps {
  onClose?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const DialogContext = React.createContext<DialogContextProps>({} as DialogContextProps);

export interface Props extends React.HTMLAttributes<HTMLDivElement> {
  open: boolean;
  children: React.ReactNode | React.ReactNode[];
  onClose?: () => void;
  hideBackdrop?: boolean;
  disableEscapeKeyDown?: boolean;
  dialogContainerProps?: React.HTMLAttributes<HTMLDivElement> & { [key: string]: unknown };
  isSidebar?: boolean;
  showCloseButton?: boolean;
  disablePortal?: boolean;
  isBlocking?: boolean;
}

const DialogInner: React.FC<Props> = (props: Props) => {
  const {
    children,
    onClose,
    className,
    disableEscapeKeyDown = false,
    isSidebar = false,
    showCloseButton = true,
    hideBackdrop = false,
    dialogContainerProps = {},
    disablePortal = false,
    ...rest
  } = props;


  const dialogRef = React.useRef<HTMLDivElement>(null);

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      if (isBackKey(event) && !disableEscapeKeyDown) {
        event.stopPropagation();
        if (onClose) {
          onClose();
        }
      }
    },
    [disableEscapeKeyDown, onClose]
  );

  React.useEffect(() => {
    if (dialogRef.current) {
      dialogRef.current.focus();
    }
  }, []);

  useEventListener('keydown', (event) => handleKeyDown(event as KeyboardEvent));

  const dialogClasses = clsx(
    styles['dialog'],
    isSidebar && styles['dialog--has-sidebar'],
    disablePortal && styles['dialog--disable-portal'],
    className
  );

  const dialogContainerClasses = clsx(
    styles['dialog-container'],
    isSidebar && styles['dialog-container--has-sidebar'],
    styles['dialog-container--scrollable'],
    disablePortal && styles['dialog-container--disable-portal'],
    dialogContainerProps.className
  );

  return (
    <DialogContext.Provider value={{ onClose }}>
      <div className={dialogClasses} role="presentation" {...rest}>
        {!hideBackdrop && <Backdrop open={true} onClick={onClose} disablePortal={disablePortal} />}
        <div
          {...dialogContainerProps}
          className={dialogContainerClasses}
          tabIndex={0}
          role="dialog"
          ref={dialogRef}
          aria-modal={true}
        >
          {IS_BROWSER && showCloseButton && onClose && (
            <IconButton
              size="xl"
              aria-label="Tilbage"
              onClick={onClose}
              className={clsx(styles['dialog__button'], styles['dialog__button--close'], {
                [styles['dialog__button--close--disable-portal']]: disablePortal
              })}
              background={false}
              data-testid={testIds.closeDialogButton}
              icon={<ArrowBackIcon />}
            >
              Tilbage
            </IconButton>
          )}

          {children}
        </div>
      </div>
    </DialogContext.Provider>
  );
};

export const Dialog: React.FC<Props> = (props: Props) => {
  const { open, disablePortal = false, isBlocking = true } = props;
  const { handleOpen, handleClose } = useDialog();

  React.useEffect(() => {
    if (!open || disablePortal) {
      return;
    }

    const rootElement = document.getElementById('root');
    if (!rootElement) {
      return;
    }

    const countAttribute = 'data-portal-dialog-open-count';
    const currentCount = Number(rootElement.getAttribute(countAttribute) ?? 0);
    const nextCount = currentCount + 1;

    rootElement.setAttribute(countAttribute, String(nextCount));
    rootElement.classList.add('dialog-open');

    return () => {
      const latestCount = Number(rootElement.getAttribute(countAttribute) ?? 0);
      const updatedCount = Math.max(latestCount - 1, 0);

      if (updatedCount === 0) {
        rootElement.removeAttribute(countAttribute);
        rootElement.classList.remove('dialog-open');
        return;
      }

      rootElement.setAttribute(countAttribute, String(updatedCount));
    };
  }, [open, disablePortal]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    if (handleOpen && isBlocking) {
      handleOpen();
    }

    return () => {
      if (handleClose && isBlocking) {
        handleClose();
      }
    };
  }, [open, handleOpen, handleClose, isBlocking]);

  if (!open) {
    return null;
  }

  if (disablePortal) {
    return <DialogInner {...props} />;
  }

  return (
    <Portal>
      <DialogInner {...props} />
    </Portal>
  );
};

export default Dialog;
